import type { InvoiceCadence } from './customer-classification';

/**
 * Maximum billable T&M service rate (per hour) on a SOW line. Single source of
 * truth shared by the server validator (`sows/validators.ts`) and the web
 * form so the cap can't drift between them.
 */
export const MAX_TM_RATE = 1000;

/**
 * How an engagement is billed. Mirrors `engagements.billing_model`.
 *
 * The vocabulary invoice generation filters on, because generation is driven
 * by what was agreed rather than by the customer record: a customer's own
 * `customer_type` and `invoice_cadence` describe the commercial relationship,
 * but only the engagement says what was actually signed and at what price.
 *
 * `metrics` is storable and editable but not yet generatable — see
 * docs/engagements.md D12 and {@link isGeneratableBillingModel}.
 */
export const BILLING_MODELS = ['retainer', 'tm', 'metrics'] as const;
export type BillingModel = (typeof BILLING_MODELS)[number];

export const BILLING_MODEL_LABELS: Record<BillingModel, string> = {
  retainer: 'Retainer',
  tm: 'Time & materials',
  metrics: 'Metrics',
};

/**
 * How often a retainer recurs. Mirrors `engagements.billing_cadence`.
 *
 * Deliberately NOT the same words as `INVOICE_CADENCES`, which says
 * `half_yearly` / `yearly`. These are the SOW's own terms and are what the SOW
 * form and the SOW line card already display, so the generation dialog uses
 * them too — a picker that renamed them would describe the agreement in words
 * the agreement does not use. {@link sowFrequencyToCadence} is the one place
 * the two vocabularies meet.
 */
export const SOW_FREQUENCIES = [
  'monthly',
  'quarterly',
  'semi_annual',
  'annual',
  'one_time',
] as const;
export type SowFrequency = (typeof SOW_FREQUENCIES)[number];

export const SOW_FREQUENCY_LABELS: Record<SowFrequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
  one_time: 'One-Time',
};

export interface SowOption<T extends string> {
  value: T;
  label: string;
}

export const BILLING_MODEL_OPTIONS: Array<SowOption<BillingModel>> = BILLING_MODELS.map(
  (value) => ({ value, label: BILLING_MODEL_LABELS[value] }),
);

/**
 * The models a generation run can actually be scoped to.
 *
 * Deliberately NOT every billing model: `metrics` is storable and editable but
 * raises nothing, so offering it as a run scope produces a run that generates
 * zero invoices and reports every agreement as out of scope — which reads as a
 * misconfigured SOW rather than as "this scope cannot bill at all".
 */
export const GENERATABLE_BILLING_MODEL_OPTIONS: Array<SowOption<BillingModel>> =
  BILLING_MODEL_OPTIONS.filter((o) => isGeneratableBillingModel(o.value));

/** @deprecated Use {@link GENERATABLE_BILLING_MODEL_OPTIONS} for run scopes,
 *  or {@link BILLING_MODEL_OPTIONS} to label a model. */
export const SOW_LINE_TYPE_OPTIONS = GENERATABLE_BILLING_MODEL_OPTIONS;

export const SOW_FREQUENCY_OPTIONS: Array<SowOption<SowFrequency>> = SOW_FREQUENCIES.map(
  (value) => ({ value, label: SOW_FREQUENCY_LABELS[value] }),
);

/**
 * A SOW frequency as the recurrence the period arithmetic understands, or
 * `null` when it has no recurrence.
 *
 * Maps rather than duplicates: `periodForRun` already handles every calendar
 * edge (February, year boundaries, quarters that don't start in January) and
 * has the tests to prove it. A second implementation keyed on SOW words would
 * be a second set of those edges to get wrong.
 *
 * `one_time` returns null — it is a real, storable frequency a user can select,
 * but it has NO recurrence, so no close date implies a period for it.
 *
 * 🔵 One-time lines will be scheduled by the forthcoming BILLING SCHEDULE,
 * which is what will say when a one-off fee is raised and for what span. Until
 * that exists they are enumerated and reported with a stated reason rather than
 * silently skipped — see `AWAITING_BILLING_SCHEDULE` in the generation service.
 */
export function sowFrequencyToCadence(frequency: SowFrequency): InvoiceCadence | null {
  switch (frequency) {
    case 'monthly':
      return 'monthly';
    case 'quarterly':
      return 'quarterly';
    case 'semi_annual':
      return 'half_yearly';
    case 'annual':
      return 'yearly';
    case 'one_time':
      // No recurrence. Deliberately not mapped to the cadence `one_time`
      // either: that would send it through `periodForRun`, which returns null
      // and produces CADENCE_NOT_DUE — a reason that reads as "not due this
      // month" when the truth is "this cannot be scheduled yet at all".
      return null;
  }
}

/**
 * A CUSTOMER's invoice cadence in the engagement's own vocabulary, or null
 * when it says nothing (or says something unrecognised).
 *
 * The inverse of {@link sowFrequencyToCadence}, and the reason an engagement's
 * NULL cadence cannot simply be read as the customer's value: the two
 * vocabularies disagree on two of the five words — `half_yearly` against
 * `semi_annual`, `yearly` against `annual`. Inheriting raw put a word into
 * the engagement's vocabulary that is not in it, which every downstream
 * comparison then silently failed to match.
 */
export function invoiceCadenceToSowFrequency(c: string | null | undefined): SowFrequency | null {
  switch (c) {
    case 'monthly':
      return 'monthly';
    case 'quarterly':
      return 'quarterly';
    case 'half_yearly':
      return 'semi_annual';
    case 'yearly':
      return 'annual';
    case 'one_time':
      return 'one_time';
    default:
      return null;
  }
}

/** True when generation can currently derive a period for this frequency.
 *  `one_time` is valid to store and to select, but not yet schedulable. */
export function isSchedulableSowFrequency(frequency: SowFrequency): boolean {
  return sowFrequencyToCadence(frequency) !== null;
}

/**
 * Plain membership guard for the SOW's own frequency vocabulary.
 *
 * Needed when narrowing a snapshotted `text` column back to the type: a row
 * written before the current vocabulary should read as unknown rather than be
 * cast into a value it does not hold.
 */
export function isSowFrequency(v: unknown): v is SowFrequency {
  return typeof v === 'string' && (SOW_FREQUENCIES as readonly string[]).includes(v);
}

/** Only retainers carry a price that can be raised without knowing the hours,
 *  so only they are generated by the retainer run. T&M bills from approved
 *  hours in arrears; `metrics` generates nothing yet (D12). */
export function isGeneratableBillingModel(v: unknown): v is BillingModel {
  return v === 'retainer';
}

/**
 * A generation run's stored scope, in words.
 *
 * The run columns are still named `customer_types` / `invoice_cadences` — what
 * they HOLD is now the SOW's vocabulary, and renaming the columns would be a
 * migration that buys nothing. This is the one place that translation happens,
 * so the run list and the run detail page cannot describe the same run
 * differently.
 *
 * An EMPTY pair means "everything", not "nothing" — the same reading the
 * planner applies, and the one the scheduled run depends on. Rendered as
 * "nothing" it would describe every nightly run as having generated no
 * invoices on purpose.
 */
export function sowScopeLabel(scope: {
  lineTypes: readonly string[];
  frequencies: readonly string[];
}): string {
  const words = [
    ...scope.lineTypes.map((v) => BILLING_MODEL_LABELS[v as BillingModel] ?? v),
    ...scope.frequencies.map((v) => SOW_FREQUENCY_LABELS[v as SowFrequency] ?? v),
  ];
  return words.length === 0 ? 'All engagements' : words.join(', ');
}
